import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DATE_FORMAT } from '@src/shared/lib';
import { parseDateInput } from '@src/shared/lib/parseDateInput';
import ClickAwayListener from '@src/shared/ui/ClickAwayListener';
import { CalendarTodayIcon } from '@src/shared/ui/icons';
import Paper from '@src/shared/ui/Paper';
import Popper from '@src/shared/ui/Popper';
import { format, isValid } from 'date-fns';
import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextInput, { type TextFieldProps } from '../TextInput';
import CalendarHeader from './CalendarHeader';
import styles from './styles';
import YearView from './YearView';

type DateInputOwnProps = {
  name?: string;
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  showCalendarIcon?: boolean;
};

// Props DateInput wires internally; consumers must not override them.
type ReservedTextInputProps =
  | 'onFocus'
  | 'onClick'
  | 'onKeyDown'
  | 'inputRef'
  | 'autoComplete'
  | 'defaultValue'
  | 'select'
  | 'SelectProps'
  | 'multiline'
  | 'rows'
  | 'minRows'
  | 'maxRows'
  | 'type'
  | 'children';

export type DateInputProps = DateInputOwnProps &
  Omit<TextFieldProps, keyof DateInputOwnProps | ReservedTextInputProps>;

function toInputString(value: Date | null | undefined): string {
  if (!value || !isValid(value)) return '';
  return format(value, DATE_FORMAT);
}

function isOutOfRange(
  date: Date,
  minDate: Date | undefined,
  maxDate: Date | undefined,
): boolean {
  if (minDate && date < startOfDay(minDate)) return true;
  if (maxDate && date > endOfDay(maxDate)) return true;
  return false;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function DateInput(props: DateInputProps) {
  const {
    name,
    value,
    onChange,
    onBlur,
    label,
    helperText,
    error,
    required,
    disabled,
    fullWidth,
    minDate,
    maxDate,
    placeholder = DATE_FORMAT.toLowerCase(),
    showCalendarIcon = true,
    slotProps,
    ...textFieldProps
  } = props;

  const [text, setText] = useState<string>(() => toInputString(value));
  const [open, setOpen] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const textFieldRootRef = useRef<HTMLElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const lastExternalValueRef = useRef<Date | null | undefined>(value);
  const skipNextFocusOpenRef = useRef(false);
  const mouseDownInPopperRef = useRef(false);
  // Tracks whether the user edited the text since the last commit/sync, so
  // blur only re-validates actual input and never invalidates a stored value.
  const dirtyRef = useRef(false);
  const [calendarView, setCalendarView] = useState<'year' | 'month' | 'day'>(
    'day',
  );
  const [yearGridStart, setYearGridStart] = useState<number>(
    () => new Date().getFullYear() - 7,
  );
  const [yearOverride, setYearOverride] = useState<number | null>(null);
  const [monthOverride, setMonthOverride] = useState<number | null>(null);

  useEffect(() => {
    const last = lastExternalValueRef.current;
    const changed =
      (last == null) !== (value == null) ||
      (last != null && value != null && last.getTime() !== value.getTime());
    if (changed) {
      setText(toInputString(value));
      lastExternalValueRef.current = value;
      dirtyRef.current = false;
    }
  }, [value]);

  const today = useMemo(() => new Date(), []);
  const parsed = useMemo(() => parseDateInput(text, today), [text, today]);

  const baseCalendarValue: Date | null =
    parsed.date && !isOutOfRange(parsed.date, minDate, maxDate)
      ? parsed.date
      : (parsed.viewMonth ?? value ?? null);
  const calendarValue: Date | null =
    (yearOverride != null || monthOverride != null) && baseCalendarValue
      ? new Date(
          yearOverride ?? baseCalendarValue.getFullYear(),
          monthOverride ?? baseCalendarValue.getMonth(),
          baseCalendarValue.getDate(),
        )
      : baseCalendarValue;

  const baseReferenceDate: Date =
    parsed.date ?? parsed.viewMonth ?? value ?? today;
  const referenceDate: Date = new Date(
    yearOverride ?? baseReferenceDate.getFullYear(),
    monthOverride ?? baseReferenceDate.getMonth(),
    1,
  );

  const commit = (next: Date | null) => {
    lastExternalValueRef.current = next;
    onChange?.(next);
  };

  const closePopup = () => {
    setOpen(false);
    setCalendarView('day');
    setYearOverride(null);
    setMonthOverride(null);
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (mouseDownInPopperRef.current) {
      mouseDownInPopperRef.current = false;
      onBlur?.(event);
      return;
    }
    if (!dirtyRef.current) {
      closePopup();
      onBlur?.(event);
      return;
    }
    dirtyRef.current = false;
    if (text.trim() === '') {
      commit(null);
      setText('');
      setInternalError(false);
    } else if (parsed.date && !isOutOfRange(parsed.date, minDate, maxDate)) {
      commit(parsed.date);
      setText(toInputString(parsed.date));
      setInternalError(false);
    } else {
      commit(null);
      setInternalError(true);
    }
    closePopup();
    onBlur?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (parsed.date && !isOutOfRange(parsed.date, minDate, maxDate)) {
        commit(parsed.date);
        setText(toInputString(parsed.date));
        setInternalError(false);
        dirtyRef.current = false;
      } else if (text.trim() !== '') {
        commit(null);
        setInternalError(true);
        dirtyRef.current = false;
      }
      closePopup();
    } else if (event.key === 'Escape') {
      closePopup();
    } else if (event.key === 'ArrowDown' && !open) {
      setOpen(true);
    }
  };

  const handleViewChange = (nextView: 'year' | 'month' | 'day') => {
    if (nextView === 'year') {
      setYearGridStart(referenceDate.getFullYear() - 7);
    }
    setCalendarView(nextView);
  };

  const handleYearSelect = (year: number) => {
    setYearOverride(year);
    setCalendarView('month');
  };

  const handleCalendarChange = (next: Date | null) => {
    if (calendarView === 'month') {
      if (next) {
        setYearOverride(next.getFullYear());
        setMonthOverride(next.getMonth());
      }
      setCalendarView('day');
      return;
    }
    if (!next) {
      commit(null);
      setText('');
    } else {
      commit(next);
      setText(toInputString(next));
    }
    dirtyRef.current = false;
    closePopup();
    if (anchorRef.current && document.activeElement !== anchorRef.current) {
      skipNextFocusOpenRef.current = true;
      anchorRef.current.focus();
    }
  };

  return (
    <>
      <TextInput
        {...textFieldProps}
        inputRef={(el: HTMLInputElement | null) => {
          anchorRef.current = el;
          textFieldRootRef.current =
            (el?.closest('.MuiTextField-root') as HTMLElement | null) ?? null;
        }}
        name={name}
        label={label}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          dirtyRef.current = true;
          setOpen(true);
          setYearOverride(null);
          setMonthOverride(null);
          if (internalError) setInternalError(false);
          if (e.target.value.trim() === '') {
            commit(null);
          }
        }}
        onFocus={() => {
          if (skipNextFocusOpenRef.current) {
            skipNextFocusOpenRef.current = false;
            return;
          }
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        helperText={helperText}
        error={error || internalError}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        autoComplete="off"
        slotProps={{
          ...slotProps,
          input: {
            ...(slotProps?.input as Record<string, unknown>),
            ...(showCalendarIcon
              ? {
                  startAdornment: (
                    <CalendarTodayIcon
                      sx={styles.calendarIcon}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setOpen((v) => !v);
                        anchorRef.current?.focus();
                      }}
                    />
                  ),
                }
              : {}),
          },
        }}
      />
      <Popper
        open={open && !disabled}
        anchorEl={textFieldRootRef.current ?? anchorRef.current}
        placement="bottom-start"
        style={styles.popper}
      >
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={(event) => {
            const target = event.target as Node | null;
            if (target && textFieldRootRef.current?.contains(target)) return;
            closePopup();
          }}
        >
          <Paper
            ref={popperRef}
            elevation={4}
            sx={styles.paper}
            onMouseDown={() => {
              mouseDownInPopperRef.current = true;
            }}
          >
            {calendarView === 'year' ? (
              <YearView
                startYear={yearGridStart}
                currentYear={referenceDate.getFullYear()}
                onPrev={() => setYearGridStart((s) => s - 15)}
                onNext={() => setYearGridStart((s) => s + 15)}
                onSelect={handleYearSelect}
                onBackToDay={() => setCalendarView('day')}
                minDate={minDate}
                maxDate={maxDate}
              />
            ) : (
              <DateCalendar
                key={`${referenceDate.getFullYear()}-${referenceDate.getMonth()}`}
                value={calendarValue}
                referenceDate={referenceDate}
                onChange={handleCalendarChange}
                minDate={minDate}
                maxDate={maxDate}
                views={['month', 'day']}
                view={calendarView}
                onViewChange={handleViewChange}
                openTo="day"
                showDaysOutsideCurrentMonth
                slots={{ calendarHeader: CalendarHeader }}
                sx={styles.calendar}
              />
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

export default DateInput;
